import { AxiosError } from 'axios';

import { apiClient } from '../api/client';

export type BiometricGrantResponse = {
  grant_token: string;
  expires_at: string;
};

export type BiometricExchangeResponse = {
  token: string;
  expires: string;
  refresh?: string;
  grant_token: string;
  grant_expires_at: string;
};

export type BiometricRevokeResponse = {
  status: 'revoked';
};

// 403 from /biometric/grant — access token was refresh- or exchange-derived.
export class BiometricFreshAuthRequiredError extends Error {
  readonly status = 403;
  constructor() {
    super('Biometric enrollment requires a fresh credential-backed session.');
    this.name = 'BiometricFreshAuthRequiredError';
  }
}

// 401 — grant invalid, family revoked, or device_id mismatched.
export class BiometricInvalidError extends Error {
  readonly status = 401;
  constructor(message = 'Biometric grant is invalid or has already been used.') {
    super(message);
    this.name = 'BiometricInvalidError';
  }
}

// 400 — request body missing required fields or invalid platform value.
export class BiometricValidationError extends Error {
  readonly status = 400;
  constructor(message = 'Biometric request missing required fields.') {
    super(message);
    this.name = 'BiometricValidationError';
  }
}

function mapAxiosStatus(err: unknown): never {
  if (err instanceof AxiosError) {
    const status = err.response?.status;
    if (status === 403) throw new BiometricFreshAuthRequiredError();
    if (status === 401) throw new BiometricInvalidError();
    if (status === 400) throw new BiometricValidationError();
  }
  throw err;
}

export const postBiometricGrant = async (args: {
  accessToken: string;
  deviceId: string;
  deviceName: string;
  platform: 'ios' | 'android';
}): Promise<BiometricGrantResponse> => {
  try {
    const response = await apiClient.post<BiometricGrantResponse>(
      '/v1/auth/biometric/grant',
      {
        device_id: args.deviceId,
        device_name: args.deviceName,
        platform: args.platform,
      },
      { headers: { Authorization: `Bearer ${args.accessToken}` } }
    );
    return response.data;
  } catch (err) {
    return mapAxiosStatus(err);
  }
};

export const postBiometricExchange = async (args: {
  grant: string;
  deviceId: string;
}): Promise<BiometricExchangeResponse> => {
  try {
    const response = await apiClient.post<BiometricExchangeResponse>(
      '/v1/auth/biometric/exchange',
      { grant: args.grant, device_id: args.deviceId }
    );
    return response.data;
  } catch (err) {
    return mapAxiosStatus(err);
  }
};

export const postBiometricRevoke = async (args: {
  grant: string;
}): Promise<BiometricRevokeResponse> => {
  try {
    const response = await apiClient.post<BiometricRevokeResponse>(
      '/v1/auth/biometric/revoke',
      { grant: args.grant }
    );
    return response.data;
  } catch (err) {
    return mapAxiosStatus(err);
  }
};
